import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  HelpCircle, 
  Briefcase, 
  Calendar, 
  Users, 
  Shield,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Filter
} from 'lucide-react';
import { 
  getAllFAQs, 
  createFAQ, 
  updateFAQ, 
  deleteFAQ, 
  getFAQCategories,
  checkDuplicateFAQs,
  removeDuplicateFAQs,
  cleanupFAQs
} from '../../services/faqService';
import { FAQ, FAQCategory } from '../../types';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';

const FAQManagementPage: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [duplicateCheck, setDuplicateCheck] = useState<any>(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const { user } = useAuthStore();
  const addNotification = useNotificationStore(s => s.addNotification);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [faqsData, categoriesData] = await Promise.all([
        getAllFAQs(),
        getFAQCategories()
      ]);
      
      setFaqs(faqsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const initializeCategories = async () => {
    try {
      // await initializeFAQCategories(); // This line was removed from the new_code, so it's removed here.
      await loadData();
      addNotification({
        type: 'info',
        title: 'Categorie inizializzate',
        message: 'Le categorie FAQ sono state create con successo'
      });
    } catch (error) {
      console.error('Error initializing categories:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Errore nell\'inizializzazione delle categorie'
      });
    }
  };

  const handleSave = async (faqData: Partial<FAQ>) => {
    if (!user?.uid) return;

    try {
      if (editingFAQ) {
        await updateFAQ(editingFAQ.id, {
          ...faqData,
          updatedBy: user.uid
        });
        addNotification({
          type: 'info',
          title: 'FAQ aggiornata',
          message: 'La FAQ è stata aggiornata con successo'
        });
      } else {
        // Assicurati che tutti i campi richiesti siano presenti
        const newFAQData = {
          question: faqData.question || '',
          answer: faqData.answer || '',
          category: faqData.category || categories[0]?.id || '',
          order: faqData.order || 1,
          isActive: faqData.isActive ?? true,
          createdBy: user.uid,
          updatedBy: user.uid
        };
        await createFAQ(newFAQData);
        addNotification({
          type: 'info',
          title: 'FAQ creata',
          message: 'La nuova FAQ è stata creata con successo'
        });
      }
      
      setEditingFAQ(null);
      setShowForm(false);
      await loadData();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Errore nel salvataggio della FAQ'
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la FAQ?`)) {
      return;
    }

    try {
      await deleteFAQ(id);
      await loadData();
      addNotification({
        type: 'info',
        title: 'FAQ eliminata',
        message: 'La FAQ è stata eliminata con successo'
      });
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Errore nell\'eliminazione della FAQ'
      });
    }
  };

  const handleCheckDuplicates = async () => {
    try {
      setLoading(true);
      const result = await checkDuplicateFAQs();
      setDuplicateCheck(result);
    } catch (error) {
      console.error('Error checking duplicates:', error);
      setError('Errore nel controllo dei doppioni');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDuplicates = async () => {
    if (!duplicateCheck || duplicateCheck.duplicates.length === 0) {
      addNotification({
        type: 'info',
        title: 'Nessun doppione',
        message: 'Nessun doppione da rimuovere'
      });
      return;
    }

    if (window.confirm(`Sei sicuro di voler rimuovere ${duplicateCheck.duplicates.reduce((acc: number, dup: any) => acc + dup.group.length - 1, 0)} FAQ duplicate?`)) {
      try {
        setCleaningUp(true);
        const result = await removeDuplicateFAQs();
        addNotification({
          type: 'info',
          title: 'Doppioni rimossi',
          message: `Rimossi ${result.removed} doppioni. Mantenute ${result.kept} FAQ uniche.`
        });
        setDuplicateCheck(null);
        await loadData();
      } catch (error) {
        console.error('Error removing duplicates:', error);
        setError('Errore nella rimozione dei doppioni');
      } finally {
        setCleaningUp(false);
      }
    }
  };

  const handleCleanupAll = async () => {
    if (window.confirm('Sei sicuro di voler eseguire la pulizia completa delle FAQ? Questo rimuoverà i doppioni e sistemerà l\'ordine.')) {
      try {
        setCleaningUp(true);
        const result = await cleanupFAQs();
        addNotification({
          type: 'info',
          title: 'Pulizia completata',
          message: `Pulizia completata!\n- Rimossi ${result.duplicatesRemoved} doppioni\n- Sistemato l'ordine di ${result.orderingFixed} FAQ\n- Totale FAQ: ${result.totalFAQs}`
        });
        setDuplicateCheck(null);
        await loadData();
      } catch (error) {
        console.error('Error cleaning up FAQs:', error);
        setError('Errore nella pulizia delle FAQ');
      } finally {
        setCleaningUp(false);
      }
    }
  };

  const getCategoryIcon = (iconName: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      HelpCircle: <HelpCircle className="w-4 h-4" />,
      Briefcase: <Briefcase className="w-4 h-4" />,
      Calendar: <Calendar className="w-4 h-4" />,
      Users: <Users className="w-4 h-4" />,
      Shield: <Shield className="w-4 h-4" />
    };
    return icons[iconName] || <HelpCircle className="w-4 h-4" />;
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const filteredFAQs = faqs.filter(faq => 
    selectedCategory === 'all' || faq.category === selectedCategory
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Gestione FAQ
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Gestisci le domande frequenti e controlla i doppioni
        </p>
      </div>

      {/* Duplicate Management Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Gestione Doppioni
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handleCheckDuplicates}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Controlla Doppioni
            </button>
            <button
              onClick={handleCleanupAll}
              disabled={cleaningUp}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Pulizia Completa
            </button>
          </div>
        </div>

        {duplicateCheck && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{duplicateCheck.totalFAQs}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Totale FAQ</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{duplicateCheck.duplicates.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Gruppi Duplicati</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{duplicateCheck.uniqueFAQs}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">FAQ Uniche</div>
              </div>
            </div>

            {duplicateCheck.duplicates.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 dark:text-white">Dettagli Doppioni:</h3>
                {duplicateCheck.duplicates.map((duplicate: any, index: number) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
                      {duplicate.reason}
                    </p>
                    <div className="space-y-1">
                      {duplicate.group.map((faq: FAQ, faqIndex: number) => (
                        <div key={faq.id} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${faqIndex === 0 ? 'bg-green-500' : 'bg-red-500'}`}></span>
                          {faq.question.substring(0, 60)}...
                          <span className="text-gray-400">({getCategoryName(faq.category)})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleRemoveDuplicates}
                  disabled={cleaningUp}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  {cleaningUp ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Rimozione in corso...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Rimuovi Doppioni ({duplicateCheck.duplicates.reduce((acc: number, dup: any) => acc + dup.group.length - 1, 0)} FAQ)
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <button
          onClick={() => {
            setEditingFAQ(null);
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nuova FAQ
        </button>
        
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-white"
        >
          <option value="all">Tutte le categorie</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* FAQ List */}
      <div className="space-y-4">
        {filteredFAQs.map((faq) => (
          <div
            key={faq.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    {getCategoryIcon(categories.find(c => c.id === faq.category)?.icon || 'HelpCircle')}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {faq.question}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {getCategoryName(faq.category)} • Ordine: {faq.order}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </div>
              
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => {
                    setEditingFAQ(faq);
                    setShowForm(true);
                  }}
                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(faq.id)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingFAQ ? 'Modifica FAQ' : 'Nuova FAQ'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingFAQ(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <FAQForm
              faq={editingFAQ}
              categories={categories}
              onSave={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingFAQ(null);
              }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 max-w-sm">
          <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

// FAQ Form Component
interface FAQFormProps {
  faq: FAQ | null;
  categories: FAQCategory[];
  onSave: (data: Partial<FAQ>) => void;
  onCancel: () => void;
}

const FAQForm: React.FC<FAQFormProps> = ({ faq, categories, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    question: faq?.question || '',
    answer: faq?.answer || '',
    category: faq?.category || categories[0]?.id || '',
    order: faq?.order || 1,
    isActive: faq?.isActive ?? true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Domanda
        </label>
        <input
          type="text"
          value={formData.question}
          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Risposta
        </label>
        <textarea
          value={formData.answer}
          onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          required
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Categoria
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            required
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ordine
          </label>
          <input
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            min="1"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Stato
          </label>
          <select
            value={formData.isActive ? 'active' : 'inactive'}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="active">Attiva</option>
            <option value="inactive">Inattiva</option>
          </select>
        </div>
      </div>
      
      <div className="flex gap-2 justify-end pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Annulla
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {faq ? 'Aggiorna' : 'Crea'}
        </button>
      </div>
    </form>
  );
};

export default FAQManagementPage; 